import React, { useMemo } from 'react'
import { Box, Stack, Typography, useTheme } from '@mui/material'
import { useTranslation } from 'react-i18next'
import { useProjectProcess, useProject } from '../../../use-projects'
import { processAndFilterData, shouldShowServiceInCommissioning } from '../../../helpers'
import { StepGroup } from './StepGroup'
import { ProcessSkeleton } from './ProcessSkeleton'
import NoContent from '../NoContent'

const Process: React.FC = () => {
  const { data: processData, loading: processLoading } = useProjectProcess()
  const { data: projectData } = useProject()
  const { t } = useTranslation()
  const theme = useTheme()

  const groupedProcesses = useMemo(() => {
    if (!processData?.length) return {}
    
    const filtered = processAndFilterData(processData, projectData?.services || [])
    
    if (filtered.commissioning && projectData?.services) {
      const commissioningProcesses = filtered.commissioning
      const validServices = projectData.services.filter(service => 
        shouldShowServiceInCommissioning(service, commissioningProcesses)
      )
      
      if (validServices.length === 0) {
        delete filtered.commissioning
      }
    }
    
    return filtered
  }, [processData, projectData])

  const stepEntries = Object.entries(groupedProcesses)
  const stepCount = stepEntries.length

  if (processLoading) {
    return (
      <Stack spacing={2}>
        <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
          {t('projects.process.title')}
        </Typography>
        <ProcessSkeleton />
      </Stack>
    )
  }

  if (!processData?.length || stepCount === 0) {
    return (
      <Stack spacing={2}>
        <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
          {t('projects.process.title')}
        </Typography>
        <NoContent label={t('projects.process.no_content')} />
      </Stack>
    )
  }

  return (
    <Stack spacing={2}>
      <Typography component="span" sx={{ fontSize: 14, fontWeight: 600 }}>
        {t('projects.process.title')}
      </Typography>
      
      <Box
        sx={{
          width: `${Math.min(stepCount * (100 / 3), 100)}%`,
          border: `1px solid ${theme.palette.grey[200]}`,
          borderRadius: 3,
          overflow: 'hidden'
        }}
      >
        <Stack direction="row" sx={{ height: '100%' }}>
          {stepEntries.map(([stepName, processes], index) => (
            <StepGroup 
              key={stepName} 
              stepName={stepName} 
              processes={processes}
              isLast={index === stepEntries.length - 1}
            />
          ))}
        </Stack>
      </Box>
    </Stack>
  )
}

export default Process